import { captureMessage } from '@sentry/react';
import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import type { ProductWithBundleId } from '~/models/CampaignModel';
import type { ProductType } from '~/models/product/ProductType';
import { ComparableSubscription } from '~/models/subscription-comparison/ComparableSubscriptions';
import type { NgtAvailabilityResponse, NgtAvailabilityResponseYes, SanityBroadbandProduct } from './internet.api';
import { NGT_PARTNER_ID } from './internet.constants';
import { getInstallationAddress } from './internet.utils';

const defaultState: InternetStoreData = {
  selectedAddress: null,
  selectedBroadBandProduct: undefined,
  selectedSubscription: undefined,
  selectedIspProduct: undefined,
  mappedAvailabilityInfo: undefined,
  availableServiceOrderProducts: undefined,
};

/** This store handles data related to broadband availability for address checks and stores mapped data required for checkout */
export const useInternetFlowStore = create<InternetStore>()(
  devtools(
    persist(
      (_set, _get) => {
        // helper for action name in devtools
        const set = (action: string, state: Partial<InternetStore>) => _set(state, undefined, action);
        return {
          ...defaultState,
          // actions
          setSelectedAddress(selectedAddress) {
            set('setSelectedAddress', { selectedAddress });
          },
          setSelectedIspProduct(product, sanity) {
            set('setSelectedIspProduct', { selectedIspProduct: { ngt: product, cms: sanity } });
          },
          setSelectedInternetProduct(selectedBroadBandProduct) {
            const { availableServiceOrderProducts } = _get();
            // Assign isp product needed for checkout right away
            const selectedIspProduct = availableServiceOrderProducts?.find((p) =>
              p.productName.startsWith(selectedBroadBandProduct.displayData.name),
            );
            // Report missing product
            if (!selectedIspProduct) {
              captureMessage(
                captureMessage('No NGT product found for selected internet product', {
                  level: 'error',
                  extra: { basketProduct: selectedBroadBandProduct, availableProducts: availableServiceOrderProducts },
                }),
              );
            }
            set('setSelectedInternetProduct', { selectedBroadBandProduct });
          },
          setSelectedSubscription(subscription) {
            set('setSelectedSubscription', { selectedSubscription: subscription });
          },
          resetState() {
            set('resetState', defaultState);
          },
          resetSelection() {
            set('resetSelection', {
              selectedBroadBandProduct: undefined,
              selectedSubscription: undefined,
              selectedIspProduct: undefined,
            });
          },
          setAvailabilityResponse(availabilityResponse) {
            // reset state
            const noBroadbandProducts =
              !availabilityResponse ||
              availabilityResponse.serviceAvailable === false ||
              !availabilityResponse.availablePackages.some((p) =>
                p.templates.some((t) => (t.categoryName || (t as any).category_name) === 'Broadband'),
              );
            if (noBroadbandProducts) {
              set('setAvailabilityResponse', {
                mappedAvailabilityInfo: undefined,
                availableServiceOrderProducts: undefined,
              });
              return;
            }
            const { deliveryStatus, installationEffort } = getInstallationAddress(availabilityResponse);
            const availableProducts = availabilityResponse.availablePackages.map((p) => {
              // biome-ignore lint/style/noNonNullAssertion: checked for availability above
              const template = p.templates.find((t) => t.categoryName === 'Broadband')!;
              // NOTE: NGT changed the API so now the "commitment" is an empty string for both variants.
              // It does seem like the cheapest price has a startOffset of 12M though, so we'll use that logic for now
              const price = template.variation.find((v) => v.startOffset === '0M') ?? template.variation[0];
              const originalPrice = template.variation.find((v) => v.startOffset === '12M') ?? template.variation[template.variation.length -1];

              return {
                partnerId: NGT_PARTNER_ID,
                productId: p.id,
                productTemplateId: template.id,
                productName: template.externalNameNoC,
                productPrice: Number.parseFloat(price.priceVat),
                originalProductPrice: Number.parseFloat(originalPrice.priceVat),
              } as const;
            });
            set('setAvailabilityResponse', {
              mappedAvailabilityInfo: {
                ...availabilityResponse.availabilityInfo,
                installationEffort,
                deliveryStatus,
              },
              availableServiceOrderProducts: availableProducts,
            });
          },
          getInternetServiceOrder() {
            const { mappedAvailabilityInfo, selectedAddress, selectedIspProduct } = _get();
            if (selectedAddress?.addrType !== 'single') {
              return undefined;
            }
            if (!mappedAvailabilityInfo || !selectedIspProduct) {
              return undefined;
            }
            const { partnerId, originalProductPrice: _ogPrice, ...product } = selectedIspProduct.ngt;
            return {
              installationAddress: selectedAddress,
              availabilityInfo: mappedAvailabilityInfo,
              partnerId,
              product,
            } satisfies InternetServiceOrder;
          },
        };
      },
      {
        name: 'rtv-zustand-internet',
        storage: createJSONStorage(() => sessionStorage),
      },
    ),
    {
      name: 'internet-flow-store',
      store: 'InternetFlowStore',
      anonymousActionType: 'internet-flow',
      enabled: true,
    },
  ),
);

(window as any).internetFlowStore = useInternetFlowStore;

/**
 * Store types
 */
type InternetStoreData = {
  selectedAddress: SingleInternetCustomerAddress | null;
  selectedBroadBandProduct: InternetProductWithBundleId | undefined;
  selectedIspProduct: {ngt: InternetServiceOrderProduct, cms: SanityBroadbandProduct} | undefined;
  selectedSubscription?: ComparableSubscription | undefined;
  mappedAvailabilityInfo: InternetAvailabilityInfo | undefined;
  availableServiceOrderProducts: InternetServiceOrderProduct[] | undefined;
};
type InternetStore = InternetStoreData & {
  // Actions
  setSelectedAddress: (address: SingleInternetCustomerAddress | null) => void;
  setSelectedIspProduct: (product: InternetServiceOrderProduct, sanity: SanityBroadbandProduct) => void;
  setSelectedInternetProduct: (product: InternetProductWithBundleId) => void;
  setSelectedSubscription: (subscription: ComparableSubscription) => void;
  setAvailabilityResponse: (response: NgtAvailabilityResponse | undefined) => void;
  resetState: () => void;
  resetSelection: () => void;
  /** Used for checkout */
  getInternetServiceOrder: () => undefined | InternetServiceOrder;
};

/**
 * Other types
 */

type PartialAddress = {
  addrType: 'multiple'; // discriminated union helper, added by us when mapping response
  city: string;
  count: number;
  country: string;
  description: string;
  municipality: string;
  number: number | null;
  postalCode: string;
  street: string;
  type: string;
};
export type SingleInternetCustomerAddress = Omit<PartialAddress, 'count' | 'addrType'> & {
  addrType: 'single'; // discriminated union helper, added by us when mapping response
  count: 1;
  // only potentially available when count=1
  apartment: string;
  houseLetter?: string;
  propertyNumber?: string;
  propertyPartNumber: string;
  north: string;
  east: string;
};

export type InternetCustomerAddress = PartialAddress | SingleInternetCustomerAddress;

export type InternetProductWithBundleId = Omit<ProductWithBundleId, 'productType'> & { productType: ProductType.Internet };
export type InternetServiceOrderProduct = {
  /** GUID, from "packages[number].id" */
  productId: string;
  /** GUID, from "packages[number].templates[number].id" */
  productTemplateId: string;
  productName: 'Fiber 50' | 'Fiber 75' | 'Fiber 100' | 'Fiber 300';
  /** From `variation[number].commitment === '12m'` */
  productPrice: number;
  /** From `variation[number].commitment === '0m'` */
  originalProductPrice: number;
  partnerId: typeof NGT_PARTNER_ID;
};
type InternetAvailabilityInfo = NgtAvailabilityResponseYes['availabilityInfo'] & { installationEffort: string; deliveryStatus: string };
type InternetServiceOrder = {
  installationAddress: SingleInternetCustomerAddress;
  availabilityInfo: InternetAvailabilityInfo;
  partnerId: number;
  product: Omit<InternetServiceOrderProduct, 'partnerId' | 'originalProductPrice'>;
};
